import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { dictionariesApi } from '@api/dictionariesApi';
import type {
  ProductFilters,
  CreateUnitRequest,
  UpdateUnitRequest,
  CreateProductRequest,
  UpdateProductRequest,
} from '@/types/dictionaries';

// ── Units of Measure ──────────────────────────────────────

export function useUnits() {
  return useQuery({
    queryKey: ['dictionaries', 'units'],
    queryFn: () => dictionariesApi.getUnits(),
  });
}

export function useCreateUnit() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateUnitRequest) => dictionariesApi.createUnit(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['dictionaries', 'units'] });
      message.success('Одиницю виміру створено');
    },
    onError: () => {
      message.error('Помилка при створенні одиниці виміру');
    },
  });
}

export function useUpdateUnit() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateUnitRequest) => dictionariesApi.updateUnit(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['dictionaries', 'units'] });
      message.success('Одиницю виміру оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні одиниці виміру');
    },
  });
}

export function useDeleteUnit() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => dictionariesApi.deleteUnit(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['dictionaries', 'units'] });
      message.success('Одиницю виміру видалено');
    },
    onError: () => {
      message.error('Не вдалось видалити — одиниця використовується');
    },
  });
}

// ── Products ──────────────────────────────────────────────

export function useProducts(mdFilters: ProductFilters) {
  return useQuery({
    queryKey: ['dictionaries', 'products', mdFilters],
    queryFn: () => dictionariesApi.getProducts(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useProductById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['dictionaries', 'products', mdId],
    queryFn: () => dictionariesApi.getProductById(mdId!),
    enabled: !!mdId,
  });
}

export function useProductSearch(mdQuery: string) {
  return useQuery({
    queryKey: ['dictionaries', 'products', 'search', mdQuery],
    queryFn: () => dictionariesApi.searchProducts(mdQuery),
    enabled: mdQuery.length >= 2,
  });
}

export function useCreateProduct() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateProductRequest) => dictionariesApi.createProduct(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['dictionaries', 'products'] });
      message.success('Позицію створено');
    },
    onError: () => {
      message.error('Помилка при створенні позиції');
    },
  });
}

export function useUpdateProduct() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateProductRequest) => dictionariesApi.updateProduct(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['dictionaries', 'products'] });
      message.success('Позицію оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні позиції');
    },
  });
}

export function useDeleteProduct() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => dictionariesApi.deleteProduct(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['dictionaries', 'products'] });
      message.success('Позицію видалено');
    },
    onError: () => {
      message.error('Помилка при видаленні позиції');
    },
  });
}
